"use client";

import { Buy } from "react-iconly";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { toast } from "sonner";

import DotsIcon from "@/assets/icons/dots.svg";
import PauseIcon from "@/assets/icons/pause.svg";
import PlayIcon from "@/assets/icons/play.svg";
import { AudioSlider } from "@/components/partials";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AudioInfoType,
  AudioTypeEnum,
  useAudioContext,
  useCart,
} from "@/context";
import { AUDIO_DURATION_LIST } from "@/lib/constants";
import { ID } from "@/lib/schema";
import {
  apiAudio,
  apiImage,
  cn,
  formatDuration,
  handleCopy,
  handleShare,
  humanizeDuration,
  removeHTML,
  storage,
} from "@/lib/utils";

type StoredItemType = Record<
  AudioInfoType["id"],
  {
    currentTime: number;
    duration: number;
  }
>;

export function TrackItem({
  hideActions,
  title,
  body,
  audioSrc,
  thumb = "/",
  id,
  albumId,
  audioDuration,
  isPaid,
  productId,
  price,
  albumProductId,
}: {
  price?: number;
  productId?: ID;
  albumProductId?: ID;
  isPaid?: boolean;
  hideActions?: boolean;
  title: string;
  body: string;
  audioSrc: string;
  thumb: string;
  id: ID;
  albumId: ID;
  audioDuration: number;
}) {
  const { addToCart, items, removeFromCart } = useCart();
  const currentItem = items.find((c) => c.productId === productId);
  const isAlbumInCart = items.some((c) => c.productId === albumProductId);

  const {
    setAudio,
    isPlaying: playing,
    audio,
    formatTime,
    currentTime,
    duration,
    togglePlayPause,
  } = useAudioContext();
  const storeKey = `${AudioTypeEnum.TRACK}.${id}` as AudioInfoType["id"];
  const inProgress = (storage.get<StoredItemType>(AUDIO_DURATION_LIST) || {})[
    storeKey
  ];
  const isCurrentAudio = audio?.id === storeKey;
  const isPlaying = playing && isCurrentAudio;
  const cartLoading = currentItem?.adding || currentItem?.removing;
  const remainedDuration = inProgress
    ? audioDuration! - inProgress.currentTime
    : audioDuration!;

  return (
    <li
      itemProp="track"
      itemScope
      itemType="https://schema.org/MusicRecording"
      className="flex gap-4 items-start"
    >
      <button
        onClick={() => {
          if (isCurrentAudio) return togglePlayPause();
          setAudio({
            id: storeKey,
            src: apiAudio(audioSrc),
            thumbnail: apiImage(thumb),
            title: title,
          });
        }}
        type="button"
      >
        <figure
          itemProp="image"
          itemScope
          itemType="https://schema.org/ImageObject"
          className="rounded-sm w-20 flex items-center justify-center overflow-hidden aspect-square relative max-sm:w-14"
        >
          <Image
            src={apiImage(thumb, "small")}
            fill
            alt=""
            itemProp="url"
            className="object-cover"
          />
          <meta itemProp="width" content="450" />
          <meta itemProp="height" content="450" />
          <div className="p-2 rounded-full bg-[#FBF9F8] z-0 w-9 h-9 flex items-center justify-center">
            {isPlaying ? (
              <>
                <PauseIcon
                  style={{ width: 16, height: 16, color: "#F96F5D" }}
                  className="max-sm:hidden"
                />
                <PauseIcon
                  style={{ width: 14, height: 14, color: "#F96F5D" }}
                  className="sm:hidden"
                />
              </>
            ) : (
              <>
                <PlayIcon
                  fill={isCurrentAudio ? "#F96F5D" : "#393837"}
                  style={{ width: 14, height: 14 }}
                  className="max-sm:hidden"
                />
                <PlayIcon
                  fill={isCurrentAudio ? "#F96F5D" : "#393837"}
                  style={{ width: 10, height: 10 }}
                  className="sm:hidden"
                />
              </>
            )}
          </div>
        </figure>
      </button>

      <div className="flex-1 min-w-0 flex flex-col justify-between gap-3.5 max-md:gap-2">
        <Link itemProp="url" href={`/albums/${albumId}/${id}`}>
          <p
            itemProp="name"
            className="font-bold text-lg leading-none max-sm:text-base max-sm:leading-none"
          >
            {title}
          </p>
        </Link>
        <Link
          className="line-clamp-2 text-[#84807D] text-xs"
          href={`/albums/${albumId}/${id}`}
        >
          <div
            itemProp="description"
            className="line-clamp-2 text-[#84807D] text-xs"
          >
            {removeHTML(body)}
          </div>
        </Link>
        <div className="flex gap-4 justify-between items-center text-xs">
          {isCurrentAudio ? (
            <div className="flex-[0_0_50%]">
              <AudioSlider size="md" />
              <div className="flex justify-between text-xs text-[#898480] mt-1">
                <span>{formatTime(currentTime)}</span>
                <span suppressHydrationWarning>
                  {currentTime >= duration ? "" : "-"}
                  {formatTime((duration || 0) - currentTime)}
                </span>
              </div>
            </div>
          ) : (
            <p
              className={cn("text-xs", {
                "text-primary": inProgress,
              })}
            >
              <time
                itemProp="timeRequired"
                dateTime={`PT${Math.floor(remainedDuration / 60)}M${Math.round(remainedDuration % 60)}S`}
                aria-label={`Time required: ${formatDuration(remainedDuration)}`}
              >
                {humanizeDuration(remainedDuration)}
              </time>{" "}
              <span className={cn({ hidden: !inProgress })}>үлдсэн</span>
            </p>
          )}
          {!hideActions && (
            <div className="flex items-center">
              {!isAlbumInCart && !isPaid && (
                <button
                  type="button"
                  className={cn(
                    "mr-4 py-1.5 px-2 rounded-2xl flex items-center gap-2 text-xs",
                    {
                      ["bg-primary text-primary-foreground"]: currentItem,
                      ["bg-[#FBF9F8]"]: !currentItem,
                    }
                  )}
                  disabled={cartLoading}
                  onClick={() => {
                    if (currentItem)
                      return removeFromCart(currentItem.productId);
                    addToCart({
                      price: price!,
                      productId: productId!,
                      productName: title,
                      productType: 1,
                      quantity: 1,
                      total: price!,
                      thumbImg: apiImage(thumb),
                    });
                  }}
                >
                  {cartLoading ? (
                    <Loader2
                      size={14}
                      className="animate-spin"
                      style={{ color: currentItem ? "white" : "inherit" }}
                    />
                  ) : (
                    <Buy
                      size={16}
                      style={{ color: currentItem ? "white" : "inherit" }}
                    />
                  )}
                  {currentItem ? "Хасах" : "Сагслах"}
                </button>
              )}
              <DropdownAction title={title} />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function DropdownAction({ title }: { title: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <DotsIcon style={{ width: 20, height: 20 }} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className="md:hidden"
          onClick={() =>
            handleShare({
              title: title,
              url: window.location.href,
              cb: (e) => {
                if (e) return toast.error(e?.message || e);
                toast.info("Линк хуулагдлаа");
              },
            })
          }
        >
          Хуваалцах
        </DropdownMenuItem>
        <DropdownMenuItem
          className="max-md:hidden"
          onClick={() =>
            window.open(
              `https://www.facebook.com/sharer/sharer.php?display=page&u=${encodeURIComponent(window.location.href)}`
            )
          }
        >
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem
          className="max-md:hidden"
          onClick={() =>
            window.open(
              `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(window.location.href)}`
            )
          }
        >
          Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleCopy(window.location.href)}>
          Линк хуулах
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TrackItemSkeleton() {
  return (
    <li className="flex gap-5 items-center animate-pulse">
      <div className="rounded-sm w-20 aspect-square bg-slate-100 max-sm:w-14" />
      <div className="flex-1 min-w-0 flex flex-col justify-between gap-2">
        <div className="h-6 w-full rounded-md bg-slate-100" />
        <div className="h-4 w-1/3 rounded-md bg-slate-100" />
        <div className="flex gap-4 justify-between items-center">
          <div className="h-4 w-1/5 rounded-md bg-slate-100" />
          <div className="h-4 w-6 rounded-md bg-slate-100" />
        </div>
      </div>
    </li>
  );
}
